import { ReactNode } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  SelectProps,
  FormControl,
  FormHelperText,
  InputLabel,
  Select as SelectUI,
  MenuItem,
  FormControlProps,
  MenuProps as MenuPropsHU,
  ListItemIcon,
} from 'material-hu';
import { styled } from '@mui/system';

const Select = styled(SelectUI)({
  '&.svgError': {
    '& svg': {
      color: 'red',
    },
  },
});

const Control = styled(FormControl)({
  width: '100%',
});

const MenuPropsDefault = {
  PaperProps: {
    style: {
      maxHeight: '30%',
      width: '70%',
      borderRadius: '0',
    },
  },
};

type Props = {
  id?: string;
  name: string;
  label?: string;
  placeholder?: string;
  variant?: 'standard' | 'outlined' | 'filled';
  options: {
    label: string;
    value: any;
    icon?: ReactNode;
  }[];
  selectProps?: SelectProps;
  formControlProps?: FormControlProps;
  rules?: any;
  defaultValue?: string;
  disabled?: boolean;
  disableUnderline?: boolean;
  renderMenuItems?: () => JSX.Element;
  onChangeSkipStep?: (param) => void | undefined;
  MenuProps?: Partial<MenuPropsHU>;
  IconComponent?: React.ElementType;
  showErrors?: boolean;
  helperText?: string;
};

function FormSelect({
  id,
  name,
  label,
  placeholder,
  variant = 'standard',
  options,
  rules,
  selectProps,
  defaultValue,
  disabled,
  formControlProps,
  renderMenuItems = null,
  onChangeSkipStep = () => null,
  MenuProps = MenuPropsDefault,
  disableUnderline = false,
  IconComponent,
  helperText: helperTextProp,
  showErrors = false,
}: Props) {
  const { control } = useFormContext();

  const getHelperText = error => {
    if (helperTextProp) {
      return helperTextProp;
    }
    if (showErrors && error?.message) return error.message;
  };

  // Only filled inputs have the prop 'disableUnderline'
  const selectVariantProps =
    variant === 'filled'
      ? {
          ...selectProps,
          disableUnderline,
        }
      : { ...selectProps };

  return (
    <Control
      disabled={disabled}
      {...formControlProps}
    >
      <InputLabel>{label}</InputLabel>
      <Controller
        control={control}
        name={name}
        rules={rules}
        defaultValue={defaultValue}
        render={({
          field: { ref, onChange, ...field },
          fieldState: { error },
        }) => (
          <>
            <Select
              id={id}
              variant={variant}
              className={error?.type === 'required' ? 'svgError' : undefined}
              label={label}
              {...field}
              {...selectVariantProps}
              error={!!error}
              inputRef={ref}
              MenuProps={MenuProps}
              onChange={e => {
                onChange(e);
                onChangeSkipStep(e);
              }}
              IconComponent={IconComponent}
            >
              {placeholder && (
                <MenuItem
                  disabled
                  value=""
                >
                  <em>{placeholder}</em>
                </MenuItem>
              )}
              {renderMenuItems && renderMenuItems()}
              {!renderMenuItems &&
                options.map(option => (
                  <MenuItem
                    value={option.value}
                    key={option.value}
                    sx={{ alignItems: 'center' }}
                  >
                    {!!option.icon && (
                      <ListItemIcon sx={{ mr: 0 }}>{option.icon}</ListItemIcon>
                    )}
                    {option.label}
                  </MenuItem>
                ))}
            </Select>
            {showErrors && (
              <FormHelperText error={!!error}>
                {getHelperText(error)}
              </FormHelperText>
            )}
          </>
        )}
      />
    </Control>
  );
}

export default FormSelect;
